// src/components/dashboard/widgets/customize-widgets.tsx
import { Reorder } from 'motion/react';
import { X, Plus, Minus, GripVertical } from 'lucide-react';
import { useAppColors } from '@/src/lib/theme';
import { WIDGET_REGISTRY, widgetAllowed } from './registry';

interface CustomizeWidgetsProps {
  /** Venue's state code (e.g. 'NSW'); widgets for other regions are hidden. */
  region?: string | null;
  activeIds: string[];
  onChange: (ids: string[]) => void;
  onClose: () => void;
}

export default function CustomizeWidgets({ activeIds, onChange, onClose, region = null }: CustomizeWidgetsProps) {
  const c = useAppColors();
  const activeDefs = activeIds.map((id) => WIDGET_REGISTRY.find((w) => w.id === id)).filter((w): w is typeof WIDGET_REGISTRY[number] => !!w && widgetAllowed(w, region));
  const availableDefs = WIDGET_REGISTRY.filter((w) => !activeIds.includes(w.id) && widgetAllowed(w, region));

  function remove(id: string) {
    onChange(activeIds.filter((i) => i !== id));
  }
  function add(id: string) {
    onChange([...activeIds, id]);
  }
  function reorder(newList: typeof activeDefs) {
    onChange(newList.map((w) => w.id));
  }

  return (
    <div className="fixed inset-0 z-[500] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl p-5 max-h-[85vh] overflow-y-auto"
        style={{ background: c.bg, border: `1px solid ${c.border}` }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold" style={{ color: c.text }}>Customize dashboard</h3>
          <button onClick={onClose}><X size={18} style={{ color: c.textFaint }} /></button>
        </div>

        <div className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: c.textFaint }}>On your dashboard</div>
        <Reorder.Group axis="y" values={activeDefs} onReorder={reorder} className="space-y-2 mb-5">
          {activeDefs.map((w) => (
            <Reorder.Item key={w.id} value={w}>
              <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl" style={{ background: c.iconBg, border: `1px solid ${c.border}` }}>
                <GripVertical size={14} style={{ color: c.textFaint }} className="cursor-grab flex-shrink-0" />
                <w.icon size={15} className="text-leaf-400 flex-shrink-0" />
                <span className="text-sm flex-1" style={{ color: c.text }}>{w.label}</span>
                <button onClick={() => remove(w.id)} className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(232,116,106,0.12)' }}>
                  <Minus size={13} style={{ color: '#E8746A' }} />
                </button>
              </div>
            </Reorder.Item>
          ))}
          {activeDefs.length === 0 && (
            <div className="text-xs text-center py-4" style={{ color: c.textFaint }}>No widgets on your dashboard — add some below.</div>
          )}
        </Reorder.Group>

        {availableDefs.length > 0 && (
          <>
            <div className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: c.textFaint }}>Add widgets</div>
            <div className="space-y-2">
              {availableDefs.map((w) => (
                <div key={w.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl" style={{ background: c.iconBg, border: `1px solid ${c.border}` }}>
                  <w.icon size={15} color={c.textFaint} className="flex-shrink-0" />
                  <span className="text-sm flex-1" style={{ color: c.text }}>{w.label}</span>
                  <button onClick={() => add(w.id)} className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: c.accentBg }}>
                    <Plus size={13} style={{ color: c.accentSoft }} />
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}